import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

import Stripe from '../stripe/Stripe';
import Vipps from '../vipps/Vipps';
import Invoice from '../invoice/Invoice';

import { getVippsEnabled } from '../../../payment/payment-selectors';
import styles from './payment-methods.module.less';
import { isUserUsingNorwegianLanguageOnProject } from '../../i18n/project-i18n-model';
import { useProject } from '../../project-hooks/useProject';

export const PAYMENT_METHOD_VIPPS = 'PAYMENT_METHOD_VIPPS';
export const PAYMENT_METHOD_STRIPE = 'PAYMENT_METHOD_STRIPE';
export const PAYMENT_METHOD_INVOICE = 'PAYMENT_METHOD_INVOICE';

const PaymentMethods = ({
    amount = 0,
    rewards = [],
    disabled,
    isCart = false,
    giverName,
    comment,
    availablePaymentMethods = [PAYMENT_METHOD_VIPPS, PAYMENT_METHOD_STRIPE, PAYMENT_METHOD_INVOICE],
}) => {
    const project = useProject();
    const stripeEnabled = useSelector((state) => state.features.stripeEnabled);
    const invoiceEnabled = useSelector(
        (state) =>
            state.features.invoiceEnabled &&
            isUserUsingNorwegianLanguageOnProject(project) &&
            !project?.profile?.invoiceDisabled
    );
    const vippsEnabled = useSelector(
        (state) => getVippsEnabled(state) && isUserUsingNorwegianLanguageOnProject(project)
    );

    const enabledPaymentMethods = useMemo(() => {
        const isPaymentMethodEnabled = (method) => {
            switch (method) {
                case PAYMENT_METHOD_STRIPE:
                    return stripeEnabled;
                case PAYMENT_METHOD_VIPPS:
                    return vippsEnabled;
                case PAYMENT_METHOD_INVOICE:
                    return invoiceEnabled;
            }
        };

        return availablePaymentMethods.filter(isPaymentMethodEnabled);
    }, [stripeEnabled, invoiceEnabled, vippsEnabled, availablePaymentMethods]);

    const renderPaymentMethod = (method, { amount, rewards, isCart }) => {
        const disabled = !amount || amount === 0;
        switch (method) {
            case PAYMENT_METHOD_STRIPE:
                return (
                    <Stripe
                        isCart={isCart}
                        amount={amount}
                        rewards={rewards}
                        disabled={disabled}
                        giverName={giverName}
                        comment={comment}
                    />
                );
            case PAYMENT_METHOD_VIPPS:
                return (
                    <Vipps
                        isCart={isCart}
                        amount={amount}
                        rewards={rewards}
                        disabled={disabled}
                        giverName={giverName}
                        comment={comment}
                    />
                );
            case PAYMENT_METHOD_INVOICE:
                return (
                    <Invoice
                        isCart={isCart}
                        amount={amount}
                        rewards={rewards}
                        disabled={disabled}
                        giverName={giverName}
                        comment={comment}
                    />
                );
        }
    };

    return (
        <fieldset disabled={disabled} aria-labelledby="choose-payment-method">
            <div className={styles.container}>
                {enabledPaymentMethods.length > 0 ? (
                    enabledPaymentMethods.map((method) => (
                        <div key={method}>
                            {renderPaymentMethod(method, {
                                amount,
                                rewards,
                                disabled,
                                isCart,
                            })}
                        </div>
                    ))
                ) : (
                    <span>Betaling er ikke tilgjengelig akkurat nå, vennligst kom innom her senere!</span>
                )}
            </div>
        </fieldset>
    );
};

PaymentMethods.propTypes = {
    amount: PropTypes.number,
    rewards: PropTypes.array,
    disabled: PropTypes.bool,
    isCart: PropTypes.bool,
};

export default PaymentMethods;
